import {getConfig} from '../support/Config';

/**
 * Публичный контур регистрации и восстановления пароля
 * (`docs/features/2026-08-20_auth-registration-recovery`).
 *
 * Отдельно от {@link ../service/AuthService}: здесь нет состояния сессии — только
 * запросы, доступные анониму. Все ответы «письмо отправлено» одинаковы независимо от
 * существования аккаунта (SEC-1), поэтому и наружу возвращается просто успех.
 */

/** Ошибка API с кодом из тела ответа: экраны различают сценарии по нему, а не по тексту. */
export class AuthApiError extends Error {
    readonly code: string;
    readonly description: string;
    readonly status: number;

    constructor(code: string, description: string, status: number) {
        super(code);
        this.name = 'AuthApiError';
        this.code = code;
        this.description = description;
        this.status = status;
    }
}

const DEFAULT_ERROR = 'Что-то пошло не так. Попробуйте позже.';

async function post<T>(path: string, body: Record<string, unknown>): Promise<T> {
    let resp: Response;
    try {
        resp = await fetch(getConfig('apiBaseUrl') + path, {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(body),
        });
    } catch {
        // Сеть недоступна — отдельный код, чтобы экран не показывал «неверная ссылка».
        throw new AuthApiError('NETWORK_ERROR', 'Нет связи с сервером. Проверьте подключение.', 0);
    }

    if (!resp.ok) {
        let code = 'UNKNOWN_ERROR';
        let description = DEFAULT_ERROR;
        try {
            const data = await resp.json();
            code = data?.error || code;
            description = data?.description || description;
        } catch {
            // Тело не JSON (например, 502 от прокси) — остаёмся с общим текстом.
        }
        throw new AuthApiError(code, description, resp.status);
    }

    if (resp.status === 204) {
        return undefined as T;
    }
    return resp.json() as Promise<T>;
}

export interface RegisterRequest {
    email: string;
    password: string;
    displayName: string;
    marketingConsent: boolean;
    /**
     * Факт принятия документов сервиса — ровно то, что отметил пользователь в форме.
     * Backend отклоняет запрос без него (C-4); подставлять `true` за пользователя нельзя.
     */
    legalConsentAccepted: boolean;
    captchaToken: string;
}

/**
 * Регистрация: ответ одинаков и для свободного, и для занятого адреса.
 *
 * Версии документов сюда не передаются: их проставляет сервер из своего реестра,
 * иначе «согласие» подделывалось бы телом запроса (C-5).
 */
export const register = (request: RegisterRequest): Promise<void> =>
    post('/auth/register', {...request});

/** Подтверждение адреса по токену из письма. */
export const verifyEmail = (token: string): Promise<{ verified: boolean }> =>
    post('/auth/verify-email', {token});

/** Повторная отправка письма подтверждения. */
export const resendVerification = (email: string, captchaToken: string): Promise<void> =>
    post('/auth/resend-verification', {email, captchaToken});

/** Запрос ссылки для смены пароля. */
export const requestPasswordReset = (email: string, captchaToken: string): Promise<void> =>
    post('/auth/password-reset/request', {email, captchaToken});

export interface PasswordResetStatus {
    valid: boolean;
    twoFactorRequired: boolean;
}

/** Проверка ссылки: жива ли и нужен ли код второго фактора. */
export const passwordResetStatus = (token: string): Promise<PasswordResetStatus> =>
    post('/auth/password-reset/status', {token});

/** Смена пароля по ссылке; `code` обязателен, если у аккаунта включена 2FA. */
export const confirmPasswordReset = (
    token: string,
    newPassword: string,
    code: string,
    captchaToken: string,
): Promise<{ changed: boolean }> =>
    post('/auth/password-reset/confirm', {token, newPassword, code, captchaToken});
